import { RTKTags, api } from '@/store/api'

import Env from '../../../env/env'

import type {
  ContactUsRequest,
  LoginRequest,
  LoginResponse,
  OtpVerificationPhoneRequest,
  OtpVerificationRequest,
  OtpVerificationResponse,
  PaymentLinkLoginRequest,
  PaymentLinkLoginResponse,
  RegisterRequest,
  RegisterResponse,
  SendOtpEmailRequest,
  SendOtpPhoneRequest,
  SendOtpPhoneResponse,
  SetPasswordRequest,
  changePasswordRequest,
  recoverPasswordRequest,
} from './Auth.types'

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // useLoginMutation,
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/auth/sign-in`,
        method: 'POST',
        body: {
          email: request.email,
          password: request.password,
        },
      }),
    }),

    // useGoogleOauthMutation,
    googleOauth: builder.mutation<LoginResponse, void>({
      query: () => ({
        url: `${Env.BASE_API_URL}/api/v1/auth/google`,
        method: 'GET',
      }),
    }),

    // useRegisterMutation,
    register: builder.mutation<RegisterResponse, RegisterRequest>({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/auth/sign-up`,
        method: 'POST',
        body: {
          email: request.email,
          password: request.password,
          language: request.language,
        },
      }),
    }),

    // useContactUsMutation,
    contactUs: builder.mutation<null, ContactUsRequest>({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/forms/public-contact-us`,
        method: 'POST',
        body: {
          name: request.name,
          email: request.email,
          message: request.message,
        },
      }),
    }),

    // useRecoverPasswordMutation,
    recoverPassword: builder.mutation<null, recoverPasswordRequest>({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/auth/recover-password`,
        method: 'POST',
        body: {
          email: request.email,
        },
      }),
    }),

    // useChangePasswordMutation,
    changePassword: builder.mutation<null, changePasswordRequest>({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/auth/change-password`,
        method: 'POST',
        body: {
          token: request.token,
          newPassword: request.newPassword,
        },
      }),
    }),

    // useLogoutMutation,
    logout: builder.mutation<void, void>({
      query: () => ({
        url: `${Env.BASE_API_URL}/api/v1/health`,
        method: 'GET',
      }),
      invalidatesTags: [
        RTKTags.userInfo,
        RTKTags.userHistory,
        RTKTags.Home,
        RTKTags.monitoringCenter,
        RTKTags.historyAccount,
        RTKTags.accountInfo,
      ],
    }),

    // useSendOtpEmailMutation,
    sendOtpEmail: builder.mutation<void, SendOtpEmailRequest>({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/auth/send-otp-email`,
        method: 'POST',
        body: {
          email: request.email,
          reason: request.reason,
        },
      }),
    }),

    // useOtpVerificationMutation,
    otpVerification: builder.mutation<
      OtpVerificationResponse,
      OtpVerificationRequest
    >({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/auth/verify-otp-email`,
        method: 'POST',
        body: {
          otp: request.otp,
          reason: request.reason,
          email: request.email,
        },
      }),
    }),

    // useSendOtpPhoneMutation,
    sendOtpPhone: builder.mutation<SendOtpPhoneResponse, SendOtpPhoneRequest>({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/auth/send-otp-sms`,
        method: 'POST',
        body: {
          user_id: request.userId,
          phone_number: request.phone,
        },
      }),
    }),

    // useOtpVerificationPhoneMutation,
    otpVerificationPhone: builder.mutation<
      OtpVerificationResponse,
      OtpVerificationPhoneRequest
    >({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/auth/verify-otp-sms`,
        method: 'POST',
        body: {
          otp: request.otp,
          phone_number: request.phone,
          email: request.email,
        },
      }),
      invalidatesTags: [RTKTags.userInfo],
    }),
    // useActivateAccountMutation,
    activateAccount: builder.mutation<void, { userId: string }>({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/auth/activate-account`,
        method: 'POST',
        body: {
          userId: request.userId,
        },
      }),
      invalidatesTags: [RTKTags.userInfo],
    }),

    // usePaymentLinkLoginMutation
    paymentLinkLogin: builder.mutation<
      PaymentLinkLoginResponse,
      PaymentLinkLoginRequest
    >({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/auth/payment-link-login`,
        method: 'POST',
        body: {
          sessionId: request.sessionId,
        },
      }),
    }),

    // useSetPasswordMutation
    setPassword: builder.mutation<
      { message: string; status: number },
      SetPasswordRequest
    >({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/auth/set-password`,
        method: 'POST',
        body: {
          password: request.password,
        },
      }),
    }),
  }),
})

export const {
  useLoginMutation,
  useGoogleOauthMutation,
  useRegisterMutation,
  useContactUsMutation,
  useRecoverPasswordMutation,
  useChangePasswordMutation,
  useLogoutMutation,
  useSendOtpEmailMutation,
  useOtpVerificationMutation,
  useSendOtpPhoneMutation,
  useOtpVerificationPhoneMutation,
  useActivateAccountMutation,
  usePaymentLinkLoginMutation,
  useSetPasswordMutation,
} = authApi
